import type { ListingHealth, ListingHealthLabel } from "types/landlord/address";

export const LISTING_HEALTH_COLUMN_ID = "listing_health";

export const LISTING_HEALTH_TIP =
  "How complete this listing's details are. Listings with more photos, a longer description, more amenities and full costs and lease details rank higher on listing sites.";

export const LISTING_HEALTH_LABEL_TEXT: Record<ListingHealthLabel, string> = {
  weak: "Weak",
  fair: "Fair",
  strong: "Strong"
};

export const LISTING_HEALTH_HEADLINE: Record<ListingHealthLabel, string> = {
  weak: "Something listing sites need is missing.",
  fair: "Everything needed is there. More details would rank it higher.",
  strong: "Complete. Listing sites get everything they look for."
};

export const LISTING_HEALTH_NOTHING_TO_IMPROVE = "Nothing to improve.";

const LISTING_HEALTH_ORDER: Record<ListingHealthLabel, number> = {
  weak: 0,
  fair: 1,
  strong: 2
};

const toListingHealthLabel = (
  value: string | null | undefined
): ListingHealthLabel | null => {
  const label = value?.toLowerCase();
  return label && label in LISTING_HEALTH_ORDER
    ? (label as ListingHealthLabel)
    : null;
};

export const listingHealthLabelOf = (
  health: ListingHealth | string | null | undefined
) => toListingHealthLabel(typeof health === "string" ? health : health?.label);

export const normalizeListingHealth = (
  health: ListingHealth | string | null | undefined
): ListingHealth | null => {
  const label = listingHealthLabelOf(health);
  if (!label) return null;
  return typeof health === "object" && health
    ? { ...health, label }
    : { label };
};

/**
 * Weak first, then Fair, then Strong; units without a label go last.
 * AG Grid negates the result for a descending sort, so `isDescending` flips the
 * rank of a missing label to keep it last either way, as the API does.
 */
export const compareListingHealth = (
  a: string | null | undefined,
  b: string | null | undefined,
  isDescending = false
) => {
  const rank = (value: string | null | undefined) => {
    const label = toListingHealthLabel(value);
    if (label) return LISTING_HEALTH_ORDER[label];
    return isDescending ? -1 : Number.MAX_SAFE_INTEGER;
  };
  return rank(a) - rank(b);
};

export const listingHealthSyncText = (pmsName: string) =>
  `We recommend making these changes in ${pmsName} so they flow into Boom on the next sync. You can also edit them here in Boom.`;
